import type { CrbTask, GroupedTask, MinifiedTask, TaskInternalExternal } from '@gcv/shared';
import { useQueryClient } from '@tanstack/react-query';

import { BANK_CRB_TASKS_KEY, BANK_TASKS_KEY, FI_TASK_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';
import {
  getTaskDirection,
  getTaskIds,
  getTasksWithoutTask,
  getTasksWithUpdates,
  isTaskInTaskList
} from 'ui/apps/fi/util/tasks';

type BankTasksKey = [string, string, TaskInternalExternal, string, string];
type BankCrbTasksKey = [string, string, string];
type CachedTasks = (MinifiedTask | GroupedTask)[];

/**
 * Keeps all caches in sync after creating, updating, or removing tasks
 */
export function useFiTaskCache() {
  const { userBankId } = useUser();
  const queryClient = useQueryClient();

  const invalidateTaskDetails = (taskIds: string[]) => {
    const updatedIds = new Set(taskIds);

    return queryClient.invalidateQueries({
      predicate: ({ queryKey, state }) =>
        queryKey[0] === FI_TASK_KEY &&
        queryKey[1] === userBankId &&
        (updatedIds.has(queryKey[2] as string) ||
          getTaskIds((state.data as CachedTasks) ?? []).some((id) => updatedIds.has(id)))
    });
  };

  const invalidateTaskLists = () => {
    queryClient.invalidateQueries({ queryKey: [BANK_TASKS_KEY] });
    queryClient.invalidateQueries({ queryKey: [BANK_CRB_TASKS_KEY] });
  };

  const applyTaskUpdates = (updatedTasks: MinifiedTask[]) => {
    queryClient.setQueriesData<CachedTasks>({ queryKey: [BANK_TASKS_KEY] }, (tasks) =>
      tasks ? getTasksWithUpdates(tasks, updatedTasks) : tasks
    );
    queryClient.setQueriesData<CrbTask[]>({ queryKey: [BANK_CRB_TASKS_KEY] }, (tasks) =>
      tasks ? getTasksWithUpdates(tasks, updatedTasks) : tasks
    );
  };

  const removeTask = (taskId: string) => {
    queryClient.setQueriesData<CachedTasks>({ queryKey: [BANK_TASKS_KEY] }, (tasks) =>
      tasks ? getTasksWithoutTask(tasks, taskId) : tasks
    );
    queryClient.setQueriesData<CrbTask[]>({ queryKey: [BANK_CRB_TASKS_KEY] }, (tasks) =>
      tasks ? getTasksWithoutTask(tasks, taskId) : tasks
    );
  };

  const addTask = (task: MinifiedTask) => {
    queryClient.getQueriesData<CachedTasks>({ queryKey: [BANK_TASKS_KEY] }).forEach(([queryKey, tasks]) => {
      const [, , taskDirection, startDate, endDate] = queryKey as BankTasksKey;

      if (tasks && isTaskInTaskList(task, { taskDirection, startDate, endDate })) {
        queryClient.setQueryData<CachedTasks>(queryKey, [...tasks, task]);
      }
    });

    queryClient.getQueriesData<CrbTask[]>({ queryKey: [BANK_CRB_TASKS_KEY] }).forEach(([queryKey, tasks]) => {
      const [, , crbId] = queryKey as BankCrbTasksKey;

      if (tasks && task.account?.id === crbId) {
        queryClient.setQueryData<CrbTask[]>(queryKey, [
          ...tasks,
          { ...task, internal_or_external: getTaskDirection(task) }
        ]);
      }
    });
  };

  return { addTask, invalidateTaskDetails, invalidateTaskLists, applyTaskUpdates, removeTask };
}
